import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Boxes,
  ChevronDown,
  ChevronUp,
  Edit3,
  ImageOff,
  PackageCheck,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import api from "../api/api";
import { useDialog } from "../components/DialogProvider";
import { EmptyState, LoadingCard, PageHeader, PremiumCard } from "../components/ui";
import { includesSearch } from "../utils/search";
import AutoBundleRulesPanel from "../components/AutoBundleRulesPanel";

function ProductIcon({ imageUrl, alt }) {
  const [broken, setBroken] = useState(false);
  const apiBase = process.env.REACT_APP_API_BASE_URL || "";

  const src = useMemo(() => {
    if (!imageUrl) return "";
    const raw = String(imageUrl);
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `${apiBase}${raw}`;
  }, [apiBase, imageUrl]);

  if (!src || broken) {
    return (
      <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-50 text-slate-400">
        <ImageOff className="h-4 w-4" />
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="h-9 w-9 rounded-xl border border-slate-200 object-cover"
      loading="lazy"
      onError={() => setBroken(true)}
    />
  );
}

function MiniStat({ icon: Icon, label, value, tone = "slate" }) {
  const toneMap = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
  };

  return (
    <div className="flex min-w-0 items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className={"flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border " + (toneMap[tone] || toneMap.slate)}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="min-w-0">
        <div className="truncate text-xs font-medium text-slate-500">{label}</div>
        <div className="mt-0.5 truncate text-lg font-semibold text-slate-950">{value}</div>
      </div>
    </div>
  );
}

function normalizeSelected(items) {
  const next = {};
  (items || []).forEach((item) => {
    const productId = Number(item?.product_id);
    const qty = Math.trunc(Number(item?.qty));
    if (Number.isInteger(productId) && productId > 0 && Number.isFinite(qty) && qty > 0) {
      next[String(productId)] = qty;
    }
  });
  return next;
}

function BundleModal({
  open,
  mode,
  form,
  setForm,
  loading,
  products,
  componentSearch,
  setComponentSearch,
  selectedComponents,
  selectedArray,
  componentsList,
  onToggle,
  onQtyChange,
  onSubmit,
  onClose,
}) {
  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-950/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
          <div>
            <div className="text-base font-semibold text-slate-950">
              {mode === "edit" ? "Edit Bundle" : "Buat Bundle Baru"}
            </div>
            <div className="mt-1 text-xs text-slate-500">Pilih komponen dan qty yang otomatis ikut berkurang saat checkout.</div>
          </div>
          <button type="button" className="btn btn-secondary px-3" onClick={onClose} disabled={loading} aria-label="Tutup">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-col">
          <div className="grid min-h-0 grid-cols-1 gap-4 overflow-y-auto px-5 py-4 lg:grid-cols-3">
            <div className="space-y-4 lg:col-span-1">
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">Nama Bundle</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Contoh: Paket Packing"
                  className="input"
                />
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                Saat Stock Keluar, sistem akan mengurangi stok produk yang di-checkout dan semua komponen bundle yang dicentang.
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
                Komponen dipilih: <span className="font-semibold">{selectedArray.length}</span>
                {selectedArray.length ? ` item, total qty default ${selectedArray.reduce((sum, x) => sum + x.qty, 0)}` : " item"}
              </div>
            </div>

            <div className="min-w-0 lg:col-span-2">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Komponen Bundle (checklist)</div>
                  <div className="text-xs text-slate-500">Cari komponen bonus/paket dan atur qty.</div>
                </div>
                <div className="relative w-full sm:w-80">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    value={componentSearch}
                    onChange={(e) => setComponentSearch(e.target.value)}
                    placeholder="Cari kode/nama, contoh: stb1"
                    className="input pl-9"
                  />
                </div>
              </div>

              <div className="table-shell mt-3 max-h-[420px] rounded-xl">
                <table className="min-w-full text-sm">
                  <thead className="table-head">
                    <tr>
                      <th className="px-3 py-3 font-semibold">Pilih</th>
                      <th className="px-3 py-3 font-semibold">Foto</th>
                      <th className="px-3 py-3 font-semibold">Komponen</th>
                      <th className="px-3 py-3 text-right font-semibold">Qty</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-slate-50/40">
                    {componentsList.length === 0 ? (
                      <tr>
                        <td className="px-3 py-4 text-slate-600" colSpan={4}>
                          Tidak ada komponen.
                        </td>
                      </tr>
                    ) : (
                      componentsList.map((p) => {
                        const key = String(p.id);
                        const checked = selectedComponents[key] != null;
                        const qty = selectedComponents[key] || 1;
                        return (
                          <tr key={p.id} className="table-row">
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={(e) => onToggle(key, e.target.checked)}
                                className="h-4 w-4 rounded border-slate-300"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <ProductIcon imageUrl={p.image_url} alt={p.nama_barang || p.kode_barang || "Komponen"} />
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-slate-900">{p.nama_barang}</div>
                              <div className="font-mono text-xs text-slate-500">{p.kode_barang}</div>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <input
                                type="number"
                                min="1"
                                disabled={!checked}
                                value={qty}
                                onChange={(e) => onQtyChange(key, e.target.value)}
                                className="input w-20 px-2 py-1 text-right disabled:bg-slate-50 disabled:text-slate-400"
                              />
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-2 text-xs text-slate-500">
                Menampilkan {componentsList.length} dari {products.length} produk.
              </div>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-slate-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-end">
            <button type="button" className="btn btn-secondary justify-center" onClick={onClose} disabled={loading}>
              Batal
            </button>
            <button disabled={loading} className="btn btn-primary justify-center">
              {loading ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-white" />
              ) : mode === "edit" ? (
                <Edit3 className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              {loading ? "Menyimpan..." : mode === "edit" ? "Simpan Perubahan" : "Simpan Bundle"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

function BomRule() {
  const dialog = useDialog();
  const [products, setProducts] = useState([]);
  const [bundlesRaw, setBundlesRaw] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [componentSearch, setComponentSearch] = useState("");
  const [bundleModalOpen, setBundleModalOpen] = useState(false);
  const [editingBundle, setEditingBundle] = useState(null);
  const [expandedBundles, setExpandedBundles] = useState({});
  const [form, setForm] = useState({ name: "" });
  const [selectedComponents, setSelectedComponents] = useState({});

  const fetchProducts = async () => {
    const res = await api.get("/products");
    setProducts(res.data || []);
  };

  const fetchBOM = async () => {
    const res = await api.get("/bom");
    setBundlesRaw(res.data || []);
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        await Promise.all([fetchProducts(), fetchBOM()]);
        setError("");
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Gagal memuat data BOM");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    (bundlesRaw || []).forEach((r) => {
      const bundleId = r.bundle_id;
      if (bundleId == null) return;
      if (!map.has(bundleId)) {
        map.set(bundleId, {
          bundle_id: r.bundle_id,
          bundle_name: r.bundle_name,
          items: [],
        });
      }

      if (r.product_id != null) {
        map.get(bundleId).items.push({
          product_id: r.product_id,
          qty: r.item_qty,
          kode_barang: r.kode_barang,
          nama_barang: r.nama_barang,
          image_url: r.image_url,
        });
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      return String(a.bundle_name || "").localeCompare(String(b.bundle_name || ""));
    });
  }, [bundlesRaw]);

  const componentsList = useMemo(() => {
    const q = componentSearch.trim();
    return products
      .filter((p) => p.kode_barang)
      .filter((p) => {
        if (!q) return true;
        return includesSearch(`${p.kode_barang || ""} ${p.nama_barang || ""}`, q);
      })
      .slice(0, 200);
  }, [products, componentSearch]);

  const selectedArray = useMemo(() => {
    return Object.entries(selectedComponents)
      .map(([product_id, qty]) => ({ product_id: Number(product_id), qty: Number(qty) || 0 }))
      .filter((x) => Number.isInteger(x.product_id) && x.product_id > 0 && x.qty > 0);
  }, [selectedComponents]);

  const totalBundleItems = useMemo(() => {
    return grouped.reduce((sum, bundle) => sum + (bundle.items?.length || 0), 0);
  }, [grouped]);

  const resetBundleModal = () => {
    setBundleModalOpen(false);
    setEditingBundle(null);
    setForm({ name: "" });
    setSelectedComponents({});
    setComponentSearch("");
  };

  const openCreateBundle = () => {
    setEditingBundle(null);
    setForm({ name: "" });
    setSelectedComponents({});
    setComponentSearch("");
    setBundleModalOpen(true);
  };

  const openEditBundle = (bundle) => {
    setEditingBundle(bundle);
    setForm({ name: bundle.bundle_name || "" });
    setSelectedComponents(normalizeSelected(bundle.items));
    setComponentSearch("");
    setBundleModalOpen(true);
  };

  const handleToggle = (productId, checked) => {
    setSelectedComponents((prev) => {
      const next = { ...prev };
      if (!checked) {
        delete next[productId];
      } else {
        next[productId] = next[productId] || 1;
      }
      return next;
    });
  };

  const handleQtyChange = (productId, value) => {
    if (value === "") {
      setSelectedComponents((prev) => ({ ...prev, [productId]: "" }));
      return;
    }
    const v = Math.trunc(Number(value));
    if (Number.isFinite(v) && v > 0) {
      setSelectedComponents((prev) => ({ ...prev, [productId]: v }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = String(form.name || "").trim();
    if (!name) {
      dialog.toast({ type: "error", title: "Bundle", message: "Nama bundle wajib diisi." });
      return;
    }
    if (selectedArray.length === 0) {
      dialog.toast({ type: "error", title: "Bundle", message: "Centang minimal 1 komponen untuk bundle." });
      return;
    }

    const isEdit = Boolean(editingBundle?.bundle_id);

    try {
      setLoading(true);
      setError("");
      const request = isEdit
        ? api.put(`/bom/${editingBundle.bundle_id}`, { name, items: selectedArray })
        : api.post("/bom", { name, items: selectedArray });

      await dialog.toastPromise(
        request,
        {
          loading: isEdit ? "Menyimpan perubahan..." : "Menyimpan bundle...",
          success: () => (isEdit ? "Bundle berhasil diupdate." : `Bundle tersimpan: ${name}.`),
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menyimpan bundle.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      await fetchBOM();
      resetBundleModal();
    } catch (e2) {
      const msg = e2?.response?.data?.message || e2.message || "Gagal menyimpan bundle";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (bundle) => {
    const ok = await dialog.confirm(
      `Yakin mau hapus bundle ini?\n\nNama: ${bundle.bundle_name}\nKomponen: ${bundle.items.length}\n\nJika bundle dipakai auto rule aktif, backend akan menolak penghapusan.`,
      { title: "Hapus Bundle", confirmText: "Hapus", cancelText: "Batal" }
    );
    if (!ok) return;

    try {
      setLoading(true);

      await dialog.toastPromise(
        api.delete(`/bom/${bundle.bundle_id}`),
        {
          loading: "Menghapus bundle...",
          success: () => "Bundle berhasil dihapus.",
          error: (e2) => e2?.response?.data?.message || e2.message || "Gagal menghapus bundle.",
        },
        { successTitle: "Berhasil", errorTitle: "Gagal" }
      );

      await fetchBOM();
      setError("");
    } catch (e2) {
      const msg = e2?.response?.data?.message || e2.message || "Gagal menghapus bundle";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const toggleDetail = (bundleId) => {
    setExpandedBundles((prev) => ({ ...prev, [bundleId]: !prev[bundleId] }));
  };

  return (
    <div className="space-y-4 animate-fade-in">
      <PageHeader
        eyebrow="Bundle Rules"
        title="Bundle"
        description="Atur komponen bundle agar stok produk dan komponen otomatis berkurang saat stock keluar."
        action={
          <button type="button" className="btn btn-primary justify-center" onClick={openCreateBundle}>
            <Plus className="h-4 w-4" />
            Buat Bundle
          </button>
        }
      />

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <MiniStat icon={Boxes} label="Total Bundle" value={grouped.length} tone="slate" />
        <MiniStat icon={PackageCheck} label="Komponen Terdaftar" value={totalBundleItems} tone="emerald" />
        <MiniStat icon={Plus} label="Dipilih Saat Ini" value={bundleModalOpen ? selectedArray.length : 0} tone="sky" />
      </div>

      <AutoBundleRulesPanel bundlesRaw={bundlesRaw} loading={loading} />

      <div className="space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold">Daftar Bundle</h2>
            <p className="text-sm text-slate-600">Bundle yang bisa diterapkan saat Stock Keluar.</p>
          </div>
          <button type="button" className="btn btn-secondary justify-center" onClick={openCreateBundle}>
            <Plus className="h-4 w-4" />
            Buat Bundle
          </button>
        </div>

        {loading && grouped.length === 0 ? (
          <LoadingCard label="Memuat bundle..." />
        ) : grouped.length === 0 ? (
          <EmptyState
            icon={Boxes}
            title="Belum ada bundle"
            description="Buat bundle pertama agar komponen otomatis ikut dipotong saat stock keluar."
            action={<button type="button" className="btn btn-primary" onClick={openCreateBundle}>Buat Bundle</button>}
          />
        ) : (
          grouped.map((g) => {
            const expanded = Boolean(expandedBundles[g.bundle_id]);
            const summary = (g.items || []).slice(0, 4).map((item) => `${item.nama_barang || item.kode_barang} x${item.qty}`);
            const remaining = Math.max(0, (g.items || []).length - summary.length);

            return (
              <PremiumCard key={g.bundle_id} className="p-0">
                <div className="flex flex-col gap-3 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{g.bundle_name}</div>
                    <div className="mt-1 text-xs text-slate-500">ID: {g.bundle_id} | Komponen: {g.items.length}</div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {summary.length ? summary.map((text, index) => (
                        <span key={`${g.bundle_id}-summary-${index}`} className="badge border-slate-200 bg-slate-50 text-slate-700">
                          {text}
                        </span>
                      )) : (
                        <span className="text-xs text-slate-500">Tidak ada komponen.</span>
                      )}
                      {remaining ? <span className="badge border-slate-200 bg-white text-slate-500">+{remaining} lainnya</span> : null}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    <button type="button" onClick={() => toggleDetail(g.bundle_id)} className="btn btn-secondary px-3 py-1.5 text-xs">
                      {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {expanded ? "Tutup Detail" : "Lihat Detail"}
                    </button>
                    <button type="button" onClick={() => openEditBundle(g)} className="btn btn-secondary px-3 py-1.5 text-xs">
                      <Edit3 className="h-3.5 w-3.5" />
                      Edit
                    </button>
                    <button type="button" onClick={() => handleDelete(g)} className="btn btn-danger px-3 py-1.5 text-xs">
                      <Trash2 className="h-3.5 w-3.5" />
                      Hapus Bundle
                    </button>
                  </div>
                </div>

                {expanded ? (
                  <div className="overflow-auto border-t border-slate-100">
                    <table className="min-w-full text-sm">
                      <thead className="table-head">
                        <tr>
                          <th className="px-4 py-3 font-semibold">Foto</th>
                          <th className="px-4 py-3 font-semibold">Komponen</th>
                          <th className="px-4 py-3 text-right font-semibold">Qty</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {g.items.map((item) => (
                          <tr key={`${g.bundle_id}-${item.product_id}`} className="table-row">
                            <td className="px-4 py-3">
                              <ProductIcon imageUrl={item.image_url} alt={item.nama_barang || item.kode_barang || "Komponen"} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="text-slate-900">{item.nama_barang || item.kode_barang}</div>
                              <div className="font-mono text-xs text-slate-500">{item.kode_barang}</div>
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-slate-900">{item.qty}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
              </PremiumCard>
            );
          })
        )}
      </div>

      <BundleModal
        open={bundleModalOpen}
        mode={editingBundle ? "edit" : "create"}
        form={form}
        setForm={setForm}
        loading={loading}
        products={products}
        componentSearch={componentSearch}
        setComponentSearch={setComponentSearch}
        selectedComponents={selectedComponents}
        selectedArray={selectedArray}
        componentsList={componentsList}
        onToggle={handleToggle}
        onQtyChange={handleQtyChange}
        onSubmit={handleSubmit}
        onClose={resetBundleModal}
      />
    </div>
  );
}

export default BomRule;
